import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Divider,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
} from "@mui/material";
import SidebarMenu from "../AdminPanel/SidebarMenu";
import TopBar from "../AdminPanel/TopBar";
import { useNavigate, useParams } from "react-router-dom";

const labelTipo = (tipo = "", origen = "") => {
  const o = String(origen || "").toUpperCase();
  const t = String(tipo || "").toUpperCase();

  if (o === "INVENTARIO") return t === "ING" ? "Ingreso" : t === "EGR" ? "Egreso" : "Inventario";
  if (o === "VENTA") return "Venta";
  if (o === "COMPRA") return "Compra";

  return o || "";
};

const labelEstado = (data) => {
  const origen = String(data?.origen || "").toUpperCase();
  const estado = String(data?.estado || "").toUpperCase();

  if (origen === "COMPRA") return "APLICADO";
  if (origen === "INVENTARIO") return estado || "PENDIENTE";
  if (origen === "VENTA") return estado || "PENDIENTE";

  return estado || "PENDIENTE";
};

const estadoSx = (estado) => {
  const e = String(estado || "").toUpperCase();

  if (e === "APLICADO" || e === "AUTORIZADO" || e === "REGISTRADA") {
    return {
      bgcolor: "#E8F5E9",
      color: "#1B5E20",
    };
  }

  if (e === "ANULADA") {
    return {
      bgcolor: "#FFEBEE",
      color: "#B71C1C",
    };
  }

  if (e === "PENDIENTE") {
    return {
      bgcolor: "#FFF8E1",
      color: "#E65100",
    };
  }

  return {
    bgcolor: "#E3F2FD",
    color: "#1565C0",
  };
};

const formatMoney = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
};

const getProductName = (it) => {
  const producto = String(it?.producto || "").trim();
  const descripcion = String(it?.descripcion || "").trim();
  const valorVariante =
    String(it?.variant_value || it?.valor_variante || it?.nombre_variante || it?.variant_name || "").trim();

  if (producto && /\(.+\)$/.test(producto)) return producto;
  if (descripcion && /\(.+\)$/.test(descripcion)) return descripcion;
  if (producto && valorVariante) return `${producto} (${valorVariante})`;

  return producto || descripcion || "-";
};

const getBodegaName = (obj) => {
  const nombre = String(obj?.bodega_nombre || "").trim();
  const codigo = String(obj?.bodega_codigo || "").trim();

  if (nombre) return nombre;
  if (codigo) return codigo;

  return "-";
};

const MotionDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const fetchDetail = async () => {
    setLoading(true);

    try {
      const r = await fetch(`/core/movimientos/${id}/`, { credentials: "include" });
      const d = await r.json().catch(() => null);
      setData(d && typeof d === "object" ? d : null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, [id]);

  const handleLogout = async () => {
    await fetch("/sistema/logout/", { method: "POST", credentials: "include" });
    localStorage.clear();
    navigate("/login");
  };

  const detalles = Array.isArray(data?.detalles) ? data.detalles : [];

  const totales = useMemo(() => {
    const totalCant = detalles.reduce((acc, it) => acc + (Number(it.cantidad) || 0), 0);

    const totalMonto = detalles.reduce((acc, it) => {
      const qty = Number(it.cantidad) || 0;
      const pu =
        Number(it.costo_unitario) ||
        Number(it.precio_unitario) ||
        Number(it.precio) ||
        0;
      const rowTotal = Number(it.total) || qty * pu;
      return acc + (Number.isFinite(rowTotal) ? rowTotal : 0);
    }, 0);

    return { totalCant, totalMonto };
  }, [detalles]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", minHeight: "100vh", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
        <Box sx={{ p: 4, width: "100%" }}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography sx={{ fontWeight: 800, mb: 1 }}>No se pudo cargar el documento</Typography>
            <Typography sx={{ color: "#757575" }}>
              Revisa que el ID exista y que el endpoint responda correctamente.
            </Typography>
          </Paper>
        </Box>
      </Box>
    );
  }

  const tipoLabel = labelTipo(data.tipo, data.origen);
  const estadoLabel = labelEstado(data);
  const bodegaName = getBodegaName(data);
  const movimientoCodigo = data.movimiento_inventario_codigo || data.codigo || "-";

  const InfoItem = ({ label, value, wide = false }) => (
    <Grid item xs={12} md={wide ? 12 : 4}>
      <Box
        sx={{
          p: 1.6,
          borderRadius: 2,
          bgcolor: "#f8fafc",
          border: "1px solid #e2e8f0",
          height: "100%",
        }}
      >
        <Typography sx={{ fontSize: 11.5, color: "#64748b", fontWeight: 800, mb: 0.55 }}>
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: 14,
            color: "#0f172a",
            fontWeight: 700,
            wordBreak: "break-word",
            whiteSpace: wide ? "pre-wrap" : "normal",
          }}
        >
          {value || "-"}
        </Typography>
      </Box>
    </Grid>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        navigate={navigate}
        handleLogout={handleLogout}
      />

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <TopBar open={open} setOpen={setOpen} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Paper
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 3,
              mb: 3,
              border: "1px solid #e2e8f0",
              boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
                mb: 2,
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 900, fontSize: 22, color: "#0f172a", lineHeight: 1.15 }}>
                  {data.codigo || "-"}
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.5, fontWeight: 600 }}>
                  {String(data.origen || "").toUpperCase()}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
                <Chip
                  label={tipoLabel}
                  sx={{
                    fontWeight: 800,
                    bgcolor: "#E3F2FD",
                    color: "#1565C0",
                    borderRadius: 2,
                  }}
                />
                <Chip
                  label={estadoLabel}
                  sx={{
                    fontWeight: 800,
                    borderRadius: 2,
                    ...estadoSx(estadoLabel),
                  }}
                />
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={1.5}>
              <InfoItem label="Fecha" value={data.fecha} />
              <InfoItem label="Responsable" value={data.responsable} />
              <InfoItem label="Bodega" value={bodegaName} />
              <InfoItem label="Referencia" value={data.referencia} />
              <InfoItem label="Documento asociado" value={data.numero_documento_asociado} />
              <InfoItem label="Movimiento inventario" value={movimientoCodigo} />
              <InfoItem label="Descripción" value={data.descripcion} wide />
            </Grid>
          </Paper>

          <Paper
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 3,
              border: "1px solid #e2e8f0",
              boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
                mb: 2,
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 900, color: "#0f172a", fontSize: 18 }}>Productos</Typography>
                <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.3 }}>
                  {detalles.length ? `${detalles.length} ítems registrados` : "Sin ítems"}
                </Typography>
              </Box>

              {detalles.length > 0 && (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip
                    label={`Cant.: ${totales.totalCant}`}
                    sx={{ fontWeight: 800, bgcolor: "#F3F6FF", color: "#1A237E", borderRadius: 2 }}
                  />
                  <Chip
                    label={`Total: $${formatMoney(totales.totalMonto || 0)}`}
                    sx={{ fontWeight: 800, bgcolor: "#E8F5E9", color: "#1B5E20", borderRadius: 2 }}
                  />
                </Box>
              )}
            </Box>

            <TableContainer
              sx={{
                borderRadius: 2,
                border: "1px solid #e2e8f0",
                overflowX: "auto",
              }}
            >
              <Table size="small" sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff", fontWeight: 800 }}>Código</TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff", fontWeight: 800 }}>Producto</TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff", fontWeight: 800 }}>Bodega</TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff", fontWeight: 800 }} align="right">
                      Cant.
                    </TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff", fontWeight: 800 }} align="right">
                      Precio Unit.
                    </TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff", fontWeight: 800 }} align="right">
                      Total
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {detalles.map((it, idx) => {
                    const qty = Number(it.cantidad) || 0;
                    const unit =
                      Number(it.costo_unitario) ||
                      Number(it.precio_unitario) ||
                      Number(it.precio) ||
                      0;
                    const rowTotal = Number(it.total) || qty * unit;
                    const productName = getProductName(it);
                    const itemBodega = getBodegaName(it);

                    return (
                      <TableRow
                        key={idx}
                        hover
                        sx={{
                          "& td": {
                            py: 1.2,
                            borderBottom: "1px solid #edf2f7",
                          },
                          "&:hover": {
                            bgcolor: "#fafcff",
                          },
                        }}
                      >
                        <TableCell>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                            {it.codigo || "-"}
                          </Typography>
                        </TableCell>

                        <TableCell
                          sx={{
                            maxWidth: 420,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={productName}
                        >
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>
                            {productName}
                          </Typography>
                        </TableCell>

                        <TableCell
                          sx={{
                            maxWidth: 180,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={itemBodega}
                        >
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1565C0" }}>
                            {itemBodega}
                          </Typography>
                        </TableCell>

                        <TableCell align="right">
                          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                            {qty}
                          </Typography>
                        </TableCell>

                        <TableCell align="right">
                          <Typography sx={{ fontSize: 13 }}>
                            ${formatMoney(unit || 0)}
                          </Typography>
                        </TableCell>

                        <TableCell align="right">
                          <Typography sx={{ fontSize: 13, fontWeight: 800 }}>
                            ${formatMoney(rowTotal || 0)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {!detalles.length && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                        <Typography sx={{ fontWeight: 800, color: "#0f172a" }}>
                          No hay productos en este documento
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default MotionDetail;